import type { HistoricAncEncounter } from "$routes/patient/[:patientId]/antenatal-care/_lib/anc-encounters";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  styled,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { format as formatDate } from "date-fns";
import type { FC } from "react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import AncEncounterCard from "./anc-encounter-card";
import { selectCurrentPregnancyEncounters } from "./pregnancy-grouping";
import type { AncVisitForTab } from "./use-anc-visits-tab-visibility";

const Container = styled("div")(({ theme }) => ({
  padding: theme.spacing(1, 0),
}));

const EmptyState = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
  color: theme.palette.text.secondary,
}));

const DialogHeader = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
}));

type Props = {
  encounters: readonly AncVisitForTab[];
  /**
   * Optional id of the encounter the user is currently editing (only set on
   * the ANC route). When provided, that encounter anchors the pregnancy
   * grouping and is excluded from the listed visits. When omitted, the most
   * recent encounter anchors the grouping and is included in the list.
   */
  currentEncounterId?: string;
};

export const AncVisitsTab: FC<Props> = ({ encounters, currentEncounterId }) => {
  const [openEncounter, setOpenEncounter] =
    useState<HistoricAncEncounter | null>(null);

  const inPregnancy = selectCurrentPregnancyEncounters(
    encounters,
    currentEncounterId,
  );

  if (inPregnancy.length === 0) {
    return (
      <Container data-testid="ancVisits.empty">
        <EmptyState variant="body2">
          <FormattedMessage
            id="patient.anc.visits.empty"
            defaultMessage="No visits recorded for this pregnancy."
          />
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container data-testid="ancVisits.list">
      <List>
        {inPregnancy.map((encounter) => (
          <ListItem
            key={encounter.id}
            button
            data-testid={`ancVisits.item.${encounter.id}`}
            onClick={() =>
              setOpenEncounter({
                ...encounter,
                meta: { created: encounter.createdAt },
              })
            }
          >
            <ListItemText
              primary={
                encounter.visitNumber != null ? (
                  <FormattedMessage
                    id="patient.anc.visits.item"
                    defaultMessage="Visit {num, number} — {date}"
                    values={{
                      num: encounter.visitNumber,
                      date: formatDate(
                        new Date(encounter.createdAt),
                        "dd MMM yyyy",
                      ),
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id="patient.anc.visits.itemNoNumber"
                    defaultMessage="Visit — {date}"
                    values={{
                      date: formatDate(
                        new Date(encounter.createdAt),
                        "dd MMM yyyy",
                      ),
                    }}
                  />
                )
              }
            />
          </ListItem>
        ))}
      </List>
      <Dialog
        open={!!openEncounter}
        onClose={() => setOpenEncounter(null)}
        fullWidth
        maxWidth="md"
        aria-labelledby="ancVisits.overlay.title"
        data-testid="ancVisits.overlay"
      >
        <DialogHeader disableTypography>
          <Typography id="ancVisits.overlay.title" variant="h6" component="h2">
            {openEncounter &&
              (openEncounter.visitNumber != null ? (
                <FormattedMessage
                  id="patient.anc.visits.overlay.title"
                  defaultMessage="ANC Visit {num, number} — {date}"
                  values={{
                    num: openEncounter.visitNumber,
                    date: formatDate(
                      new Date(openEncounter.createdAt),
                      "dd MMM yyyy",
                    ),
                  }}
                />
              ) : (
                <FormattedMessage
                  id="patient.anc.visits.overlay.titleNoNumber"
                  defaultMessage="ANC Visit — {date}"
                  values={{
                    date: formatDate(
                      new Date(openEncounter.createdAt),
                      "dd MMM yyyy",
                    ),
                  }}
                />
              ))}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setOpenEncounter(null)}
            data-testid="ancVisits.overlay.close"
          >
            <CloseIcon />
          </IconButton>
        </DialogHeader>
        <DialogContent>
          {openEncounter && (
            <AncEncounterCard encounter={openEncounter} readOnly />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};
